// Alertas del piso y stock crítico del Home. Los dos son listas cortas que llevan a otra pantalla.
import { Link } from '@tanstack/react-router';
import { Clock, Link2Off, Store, User, X, type LucideIcon } from 'lucide-react';
import { ErrorState, Skeleton } from '@/kit';
import type {
    InventoryLensSkuItem,
    InventoryLensUnavailableReason,
} from '@/api/services/retail-catalog/backoffice';
import {
    sortCritical,
    TOP_MARKERS_HINT,
    unavailableReasonText,
} from '../../inventario/lib/inventarioLens';
import { LaCentralButton, type LaCentralTarget } from '../../inventario/components/LaCentralAction';
import type { FloorAlertKind, FloorAlertSourceKey, FloorAlertsResult } from '../lib/floorAlerts';

const ALERT_ICON: Record<FloorAlertKind, LucideIcon> = {
    STORE_BEHIND: Store,
    SELLER_LOW: User,
    PROMO_EXPIRING: Clock,
    CANCELLATION_TODAY: X,
};

const SOURCE_ERROR: Record<FloorAlertSourceKey, string> = {
    stores: 'No se pudieron revisar las tiendas rezagadas.',
    sellers: 'No se pudieron revisar los vendedores con avance bajo.',
    promotions: 'No se pudieron revisar las promociones.',
    cancellations: 'No se pudieron revisar las cancelaciones de hoy.',
};

const SOURCE_UNAVAILABLE: Record<FloorAlertSourceKey, string> = {
    stores: 'No tienes acceso a las tiendas; no se revisaron las rezagadas.',
    sellers: 'No tienes acceso a los vendedores; no se revisó su avance.',
    promotions: 'No tienes acceso a las promociones; no se revisaron sus vencimientos.',
    cancellations: 'No tienes acceso a las cancelaciones; no se revisaron las de hoy.',
};

/** Un bloque que no se pudo consultar; dice el motivo para no confundirlo con un vacío. */
function NoSourcePanel({ children }: { children: React.ReactNode }) {
    return (
        <div className="m-2 flex items-start gap-2 rounded-lg border border-warning-soft-border bg-warning-soft p-3 text-[11px] text-warning">
            <Link2Off className="mt-px size-3.5 flex-none" aria-hidden />
            <div>
                <div className="text-[11.5px] font-semibold">Sin fuente para consultar</div>
                <p className="mt-1 leading-relaxed">{children}</p>
                <p className="mt-1 leading-relaxed opacity-80">
                    No es lo mismo que «no hay»: nadie preguntó.
                </p>
            </div>
        </div>
    );
}

/** Alertas compuestas en el front; cada fuente caída o cargando se dice aparte. */
export function HomeAlerts({ result }: { result: FloorAlertsResult }) {
    const { alerts, unchecked, loading } = result;
    if (alerts.length === 0 && unchecked.length === 0 && loading.length > 0) {
        return (
            <div className="p-3">
                <Skeleton lines={3} />
            </div>
        );
    }
    const allChecked = unchecked.length === 0 && loading.length === 0;
    return (
        <div className="flex flex-col">
            {alerts.map((alert) => {
                const Icon = ALERT_ICON[alert.kind];
                return (
                    <div
                        key={alert.id}
                        className="flex items-start gap-2 border-b border-border px-3 py-2 text-[11px] last:border-b-0"
                    >
                        <Icon
                            className="mt-0.5 size-2.5 flex-none text-muted-foreground"
                            aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{alert.title}</div>
                            <div className="truncate text-[10px] text-muted-foreground">
                                {alert.subtitle}
                            </div>
                        </div>
                        {alert.link && (
                            <Link
                                to="/retail-admin/$section"
                                params={{ section: alert.link.section }}
                                search={alert.link.search}
                                className="flex-none text-[10.5px] text-primary hover:underline"
                            >
                                Abrir
                            </Link>
                        )}
                    </div>
                );
            })}
            {allChecked && alerts.length === 0 && (
                <div className="py-6 text-center text-[11px] italic text-muted-foreground">
                    Sin alertas en el piso
                </div>
            )}
            {unchecked.map(({ source, reason }) => (
                <p key={source} className="px-3 py-1 text-[10.5px] italic text-warning">
                    {reason === 'error' ? SOURCE_ERROR[source] : SOURCE_UNAVAILABLE[source]}
                </p>
            ))}
            {loading.length > 0 && (
                <p className="px-3 py-1 text-[10.5px] italic text-muted-foreground">
                    Revisando el resto del piso…
                </p>
            )}
        </div>
    );
}

export function HomeStock({
    items,
    unavailable,
    reason,
    isLoading,
    error,
    onRetry,
    onOpenTransfer,
}: {
    items?: InventoryLensSkuItem[];
    unavailable: boolean;
    reason: InventoryLensUnavailableReason | null;
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
    /** Sin `retail.inventory.update` no se sugiere el traspaso: el rol no puede registrarlo. */
    onOpenTransfer: ((target: LaCentralTarget) => void) | null;
}) {
    if (error) {
        return (
            <ErrorState
                className="m-2"
                title="No se pudo cargar el stock crítico"
                description={error.message}
                onRetry={onRetry}
            />
        );
    }
    if (isLoading) {
        return (
            <div className="p-3">
                <Skeleton lines={4} />
            </div>
        );
    }
    if (unavailable) {
        return <NoSourcePanel>{unavailableReasonText(reason)}</NoSourcePanel>;
    }
    // Solo los 8 más urgentes: el Home resume, la pantalla de Inventario tiene la lista entera.
    const rows = sortCritical(items ?? []).slice(0, 8);
    if (rows.length === 0) {
        return (
            <div className="py-6 text-center text-[11px] italic text-muted-foreground">
                Sin stock crítico con demanda hoy.
            </div>
        );
    }
    return (
        <div className="px-2 py-1">
            {/* El mockup marca «Top SK» y «Top LUGA» en cada fila; el endpoint no manda ninguno. */}
            <p className="px-1 pb-1 text-[9.5px] leading-snug text-muted-foreground">
                {TOP_MARKERS_HINT}
            </p>
            {rows.map((item) => (
                <div
                    key={`${item.sku}-${item.storeId}`}
                    className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 rounded px-1 py-1 text-[11px] transition-colors hover:bg-accent/50"
                >
                    <Link
                        to="/retail-admin/$section"
                        params={{ section: 'inventario' }}
                        search={{ tab: 'critico' }}
                        className="min-w-0"
                    >
                        <div className="truncate font-medium">{item.productName}</div>
                        {/* El SKU, no la tienda: el lente manda el id de tienda y no su nombre. */}
                        <div
                            className="truncate text-[9.5px] text-muted-foreground"
                            title={`Tienda ${item.storeId}`}
                        >
                            {item.sku}
                        </div>
                    </Link>
                    <span className="text-right text-[9.5px] text-muted-foreground">
                        {item.soldTodayUnits}
                        <span className="block">hoy</span>
                    </span>
                    <span className="w-8 text-right font-bold tabular-nums text-destructive">
                        {item.stock}
                        <span className="block text-[9.5px] font-normal text-muted-foreground">
                            stock
                        </span>
                    </span>
                    {onOpenTransfer ? (
                        <LaCentralButton
                            target={{ action: 'transfer', sku: item.sku, toStoreId: item.storeId }}
                            onOpen={onOpenTransfer}
                        >
                            Sugerir traspaso
                        </LaCentralButton>
                    ) : (
                        <span />
                    )}
                </div>
            ))}
        </div>
    );
}
